import { useAPI } from "@humansignal/core";
import { Button, useToast } from "@humansignal/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SampleTable, listed, plural, why } from "./shared";
import "./Datasets.prefix.css";
import "./ReuseData.prefix.css";

const c = (name) => `lsf-reuse-data${name ? `__${name}` : ""}`;

/**
 * Import tab: make this project's tasks from a dataset's items. Nothing is
 * copied; a second run brings in only what the dataset has gained since.
 */
export const ReuseDataPanel = ({ project, labelConfig, deferred, state, setState }) => {
  const api = useAPI();
  const toast = useToast();
  const [datasets, setDatasets] = useState(null);
  // In the New Project wizard the choice is kept by the wizard, and the
  // dataset is brought in when the project is saved.
  const [ownId, setOwnId] = useState("");
  const datasetId = deferred ? String(state?.dataset ?? "") : ownId;
  const setDatasetId = (id) => (deferred ? setState?.(id ? { dataset: Number(id) } : null) : setOwnId(id));
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  // Whether the answers an item already carries open the task.
  const [start, setStart] = useState(false);
  // Bumped after data is brought in, so the plan is asked for again.
  const [round, setRound] = useState(0);

  const dataset = useMemo(
    () => (datasets ?? []).find((d) => String(d.id) === String(datasetId)),
    [datasets, datasetId],
  );

  const loadDatasets = useCallback(async () => {
    if (!project?.id) return;
    const res = await api.callApi("projectDatasets", { params: { pk: project.id }, errorFilter: () => true });
    setDatasets(res && !res.error ? (res.datasets ?? []) : []);
  }, [api, project?.id]);

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  useEffect(() => {
    if (!project?.id || !datasetId) {
      setPlan(null);
      return;
    }
    let stale = false;
    (async () => {
      const res = await api.callApi("reusePlan", {
        params: { pk: project.id },
        body: { dataset: Number(datasetId), ...(labelConfig ? { label_config: labelConfig } : {}) },
        errorFilter: () => true,
      });
      if (!stale) setPlan(res && !res.error ? res : null);
    })();
    return () => {
      stale = true;
    };
  }, [api, project?.id, datasetId, labelConfig, round]);

  const bring = async () => {
    setBusy(true);
    const res = await api.callApi("reuseBring", {
      params: { pk: project.id },
      body: { dataset: Number(datasetId), pre_annotate: start },
      errorFilter: () => true,
    });
    setBusy(false);
    if (!res || res.error) {
      toast?.show({ message: why(res, "The dataset could not be brought in."), type: "error" });
      return;
    }
    toast?.show({
      message:
        `${plural(res.created, "task", "tasks")} added` +
        (res.skipped ? `, ${res.skipped} left out (they would open with nothing to show)` : "") +
        "." +
        (res.started
          ? ` ${plural(res.started, "task opens", "tasks open")} with the answers already in the dataset.`
          : ""),
    });
    await loadDatasets();
    setRound((n) => n + 1);
  };

  if (datasets === null) return <div className={c()}>Looking for datasets…</div>;
  if (!datasets.length) {
    return (
      <div className={c()}>
        <p className={c("note")}>
          No dataset in this organisation has items yet. <a href="/datasets">Create one</a>, add a project's data to it,
          and it can be annotated here.
        </p>
      </div>
    );
  }

  const usable = plan ? plan.new - plan.unfit : 0;
  const blocked = plan?.lacking?.length > 0;

  return (
    <div className={c()} data-testid="reuse-data">
      <div className={c("row")}>
        <label className={c("field")}>
          <span>Dataset</span>
          <select value={datasetId} onChange={(e) => setDatasetId(e.target.value)} data-testid="reuse-dataset">
            <option value="">Choose a dataset</option>
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({plural(d.items, "item", "items")})
              </option>
            ))}
          </select>
        </label>
        {dataset && (
          <p className={c("note")}>
            {dataset.description || listed(dataset.holds) || ""} <a href={`/datasets/${dataset.id}`}>Open dataset</a>
          </p>
        )}
      </div>

      {plan && (
        <div className={c("plan")} data-testid="reuse-plan">
          <p>
            {plural(plan.new, "new task", "new tasks")}
            {plan.already ? ` (${plan.already} already in this project)` : ""}
            {plan.fields.length ? `, with ${listed(plan.fields)}.` : "."}
          </p>
          {blocked && (
            <p className={c("warn")}>
              The labeling setup shows {listed(plan.lacking)}, which this dataset does not have. Change the setup, or
              choose another dataset.
            </p>
          )}
          {!blocked && plan.unfit > 0 && (
            <p>{plural(plan.unfit, "has", "have")} nothing where the media goes and will be left out.</p>
          )}
          {plan.notes.map((note) => (
            <p key={note} className={c("warn")}>
              {note}
            </p>
          ))}
          {plan.sample.length > 0 && <SampleTable fields={plan.fields} rows={plan.sample} />}
          {plan.startable > 0 && (
            <label className={c("start")} data-testid="reuse-pre-annotate">
              <input type="checkbox" checked={start} onChange={(e) => setStart(e.target.checked)} />
              <span>
                Open each task with the answers already in the dataset ({plan.startable} of {plan.new}). A person checks
                and corrects them instead of starting from nothing.
              </span>
            </label>
          )}
          {deferred ? (
            <p className={c("note")} data-testid="reuse-on-save">
              {usable > 0
                ? `${plural(usable, "task", "tasks")} will be added when you save the project.`
                : "Nothing to add."}{" "}
              The files stay where they are; nothing is copied.
            </p>
          ) : (
            <>
              <div>
                <Button
                  size="small"
                  onClick={bring}
                  waiting={busy}
                  disabled={busy || blocked || usable < 1}
                  aria-label="Add the tasks"
                  data-testid="reuse-bring"
                >
                  {usable > 0 ? `Add ${plural(usable, "task", "tasks")}` : "Nothing new to add"}
                </Button>
              </div>
              <p className={c("note")}>The files stay where they are; nothing is copied.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
